import { computed, inject, Signal } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { BEStatus } from '@qtek/core/api-core';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
  WsEntityMessage,
} from '@qtek/core/websockets-core';
import { NormalSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import {
  EntityMeta,
  FinalizeSummaryRequest,
  Note,
  NoteWriteValue,
} from '@qtek/shared/models';
import {
  catchError,
  EMPTY,
  filter,
  mergeMap,
  pipe,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { NoteAiSummaryService } from './note-ai-summary.service';
import { NoteCompanyService } from './note-company.service';
import { NoteUserService } from './note-user.service';
import {
  addNote,
  noteInitialState,
  removeNote,
  replaceNote,
} from './note.updaters';
import { NoteService } from './note.service';

export type NoteSource = 'deal' | 'user' | 'company';

export type ConnectNotesPayload = Omit<
  SubWebsocketMessageRequest,
  'ent' | 'op' | 'prms'
> & { prms?: Record<string, string>; source?: NoteSource };

interface NoteWsStatus extends BEStatus {
  meta?: EntityMeta;
}

type NoteWsMessage =
  | WsEntityMessage<Note[], NoteWsStatus, 'query'>
  | WsEntityMessage<Note, NoteWsStatus, 'ins' | 'upd' | 'del'>;

export const NoteStore = signalStore(
  { providedIn: 'root' },
  withState(noteInitialState),
  withProps(() => ({
    mutationRequested$: new Subject<void>(),
    finalizeSummarySuccess$: new Subject<void>(),
  })),
  withMethods(
    (
      store,
      noteService = inject(NoteService),
      noteUserService = inject(NoteUserService),
      noteCompanyService = inject(NoteCompanyService),
      aiSummaryService = inject(NoteAiSummaryService),
      snackBar = inject(SnackBarService)
    ) => {
      const disconnect$ = new Subject<string>();
      const notify = (message: string) =>
        snackBar.showSnackBar({
          type: 'NormalMessage',
          message,
        } as NormalSnackBarData);
      const services = {
        deal: noteService,
        user: noteUserService,
        company: noteCompanyService,
      };

      // `ins`/`upd` echoes carry the plain write shape (no author/accounts/tags);
      // replay a shaped subscription's query to get the details rows back.
      const refreshAfterMutation = (mysid: string) => {
        const { prms, view } = store.notesQueryParams()[mysid] ?? {};
        if (prms || view) noteService.query({ mysid, prms, view });
      };

      const rememberQuery = (
        mysid: string,
        prms?: Record<string, string>,
        view?: string
      ) =>
        patchState(store, state => ({
          notesQueryParams: {
            ...state.notesQueryParams,
            [mysid]: { prms, view },
          },
        }));

      const handleMessage = (message: BaseWebsocketMessage) => {
        const data = message as NoteWsMessage;
        const { mysid } = data;
        switch (data.op) {
          case 'query':
            if (data.sts?.sts === 'ERR') {
              patchState(store, { notesLoading: false });
              return;
            }
            patchState(store, state => ({
              notes: { ...state.notes, [mysid]: data.res ?? [] },
              notesMeta: data.sts?.meta ?? state.notesMeta,
              notesLoading: false,
            }));
            return;
          case 'ins':
            if (!data.res) return;
            patchState(store, state => addNote(state, mysid, data.res as Note));
            notify('FTR_NOTE_ADDED');
            refreshAfterMutation(mysid);
            return;
          case 'upd':
            if (!data.res) return;
            patchState(store, state =>
              replaceNote(state, mysid, data.res as Note)
            );
            notify('FTR_NOTE_UPDATED');
            refreshAfterMutation(mysid);
            return;
          case 'del':
            if (!data.id) return;
            patchState(store, state =>
              removeNote(state, mysid, data.id as string)
            );
            notify('FTR_NOTE_REMOVED');
            return;
        }
      };

      return {
        connect: rxMethod<ConnectNotesPayload>(
          pipe(
            tap(({ mysid, prms, view }) => {
              disconnect$.next(mysid);
              patchState(store, { notesLoading: true });
              rememberQuery(mysid, prms, view);
            }),
            mergeMap(({ source = 'deal', ...payload }) =>
              services[source].subscribe(payload).pipe(
                takeUntil(disconnect$.pipe(filter(id => id === payload.mysid))),
                tap(handleMessage),
                catchError(() => {
                  patchState(store, { notesLoading: false });
                  return EMPTY;
                })
              )
            )
          )
        ),

        disconnect(mysid: string): void {
          disconnect$.next(mysid);
        },

        query(payload: {
          mysid: string;
          prms?: Record<string, string>;
          view?: string;
        }): void {
          rememberQuery(payload.mysid, payload.prms, payload.view);
          noteService.query(payload);
        },

        createNote(
          note: NoteWriteValue,
          refId: string | null,
          mysid: string
        ): void {
          noteService.createNote(note, refId, mysid);
          store.mutationRequested$.next();
        },

        updateNote(note: NoteWriteValue, mysid: string): void {
          noteService.updateNote(note, mysid);
          store.mutationRequested$.next();
        },

        deleteNote(id: string, mysid: string): void {
          noteService.deleteNote(id, mysid);
          store.mutationRequested$.next();
        },

        generateSummary: rxMethod<string[]>(
          pipe(
            tap(() =>
              patchState(store, {
                aiSummaryLoading: true,
                aiSummaryResponse: null,
              })
            ),
            switchMap(noteIds =>
              aiSummaryService.generateSummary({ noteIds }).pipe(
                tap(response =>
                  patchState(store, {
                    aiSummaryLoading: false,
                    aiSummaryResponse: response,
                  })
                ),
                catchError(() => {
                  patchState(store, { aiSummaryLoading: false });
                  return EMPTY;
                })
              )
            )
          )
        ),

        finalizeSummary: rxMethod<FinalizeSummaryRequest>(
          pipe(
            tap(() => patchState(store, { aiSummaryFinalizeLoading: true })),
            mergeMap(request =>
              aiSummaryService.finalizeSummary(request).pipe(
                tap(() => {
                  patchState(store, { aiSummaryFinalizeLoading: false });
                  store.finalizeSummarySuccess$.next();
                }),
                catchError(() => {
                  patchState(store, { aiSummaryFinalizeLoading: false });
                  return EMPTY;
                })
              )
            )
          )
        ),

        clearSummary(): void {
          patchState(store, {
            aiSummaryResponse: null,
            aiSummaryLoading: false,
            aiSummaryFinalizeLoading: false,
          });
        },

        notesFor(mysid: string): Signal<Note[]> {
          return computed(() => store.notes()[mysid] ?? []);
        },
      };
    }
  )
);
